/**
 * SponsorForm — post-purchase logo/link upload for homepage sponsors.
 * Token-gated: only unlocks after a paid sponsor_homepage_monthly checkout.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { upsertSponsor, fetchMySponsor } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorMessage } from "@/lib/errorMessage";

const EMPTY = { display_name: "", tagline: "", website_url: "", logo_url: "" };

export default function SponsorForm({ token, expiresAt }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetchMySponsor(token)
      .then(({ sponsor }) => {
        if (sponsor) {
          setForm({
            display_name: sponsor.display_name || "",
            tagline: sponsor.tagline || "",
            website_url: sponsor.website_url || "",
            logo_url: sponsor.logo_url || "",
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!form.display_name || !form.website_url) {
      toast.error("Display name and website URL are required.");
      return;
    }
    setSaving(true);
    try {
      await upsertSponsor({ token, ...form });
      toast.success("Sponsor entry saved — you're live on the homepage.");
    } catch (e) {
      toast.error(errorMessage(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const expDate = expiresAt ? new Date(expiresAt * 1000).toLocaleDateString() : null;

  return (
    <div className="bg-white border border-[#E5E2DC] rounded-2xl p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-medium text-[#1C2B23]">Sponsor entry</h2>
          <p className="text-sm text-[#5C6B62] mt-1">
            Your logo appears on the homepage &quot;Trusted by&quot; strip. Edit anytime.
          </p>
        </div>
        {expDate && (
          <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#2B5339] bg-[#F0EFEA] rounded-full px-3 py-1.5">
            active until {expDate}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#5C6B62]">
          <Loader2 size={14} className="animate-spin" /> Loading current entry…
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Display name*
            </Label>
            <Input
              data-testid="sponsor-name"
              value={form.display_name}
              onChange={(e) => set("display_name", e.target.value)}
              placeholder="Acme Wealth Advisors"
              maxLength={60}
              className="mt-1.5 bg-[#F9F8F6] border-[#E5E2DC]"
            />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Tagline (optional)
            </Label>
            <Input
              data-testid="sponsor-tagline"
              value={form.tagline}
              onChange={(e) => set("tagline", e.target.value)}
              placeholder="Fee-only fiduciary advisors for high-net-worth families"
              maxLength={90}
              className="mt-1.5 bg-[#F9F8F6] border-[#E5E2DC]"
            />
            <div className="text-[11px] text-[#5C6B62] mt-1">
              {form.tagline.length}/90 · shown on logo hover
            </div>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Website URL*
            </Label>
            <Input
              data-testid="sponsor-url"
              value={form.website_url}
              onChange={(e) => set("website_url", e.target.value)}
              placeholder="https://acmewealth.com"
              className="mt-1.5 bg-[#F9F8F6] border-[#E5E2DC]"
            />
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Logo URL
            </Label>
            <Input
              data-testid="sponsor-logo"
              value={form.logo_url}
              onChange={(e) => set("logo_url", e.target.value)}
              placeholder="https://acmewealth.com/logo.svg"
              className="mt-1.5 bg-[#F9F8F6] border-[#E5E2DC]"
            />
            <div className="text-[11px] text-[#5C6B62] mt-1">
              PNG or SVG, transparent background preferred. If blank, we show your name in text.
            </div>
            {form.logo_url && (
              <div className="mt-3 bg-[#F0EFEA] rounded-lg p-3 inline-flex items-center gap-2">
                <span className="text-[10px] text-[#5C6B62] uppercase tracking-wider">Preview</span>
                <img
                  src={form.logo_url}
                  alt={form.display_name}
                  className="h-6 w-auto object-contain"
                  onError={(e) => (e.target.style.display = "none")}
                />
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-[#E5E2DC]">
            <button
              data-testid="sponsor-save"
              type="button"
              onClick={save}
              disabled={saving}
              className="bg-[#2B5339] text-white rounded-full px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:bg-[#1E3B28] disabled:opacity-60"
            >
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save sponsor entry
            </button>
            {form.website_url && (
              <a
                href={form.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-[#5C6B62] hover:text-[#2B5339] inline-flex items-center gap-1"
              >
                Preview link <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
